import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuGroup,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuSub,
  DropdownMenuSubContent,
  DropdownMenuSubTrigger,
  DropdownMenuTrigger,
  SidebarMenu,
  SidebarMenuButton,
  SidebarMenuItem
} from '@repo/ui'
import { Link, useRouter, useRouterState } from '@tanstack/react-router'
import { BadgeCheck, ChevronRight, ChevronsUpDown, LogOut, Monitor, Moon, Sun, Users } from 'lucide-react'
import { useEffect, useRef, useState } from 'react'
import { apiLogout } from '@/api/user/logout'
import * as SentryClient from '@/sentryClient'
import { isConversationWorkspaceAvailable } from '@/features/conversation/availability'
import { useScopedSettings } from '@/features/settings/ScopedSettings'

type ThemePreference = 'system' | 'light' | 'dark'

export function NavUser({ onMenuItemClick, currentOrgSlug }: { onMenuItemClick?: () => void; currentOrgSlug: string }) {
  const router = useRouter()
  const user = router.options.context.user
  const { openSettings } = useScopedSettings()
  const currentPathname = useRouterState({ select: (s) => s.location.pathname })
  const menuTriggerRef = useRef<HTMLButtonElement>(null)
  const [theme, setTheme] = useState<ThemePreference>(() => {
    const saved = window.localStorage.getItem('sv-theme')
    return saved === 'light' || saved === 'dark' ? saved : 'system'
  })

  const [isDark, setIsDark] = useState(() => {
    const saved = window.localStorage.getItem('sv-theme')
    if (saved === 'dark') return true
    if (saved === 'light') return false
    return window.matchMedia('(prefers-color-scheme: dark)').matches
  })

  useEffect(() => {
    const mq = window.matchMedia('(prefers-color-scheme: dark)')
    const handler = (e: MediaQueryListEvent) => {
      if (theme === 'system') {
        document.documentElement.classList.toggle('dark', e.matches)
        setIsDark(e.matches)
      }
    }
    mq.addEventListener('change', handler)
    return () => mq.removeEventListener('change', handler)
  }, [theme])

  function applyTheme(next: ThemePreference) {
    setTheme(next)
    if (next === 'system') {
      window.localStorage.removeItem('sv-theme')
      const prefersDark = window.matchMedia('(prefers-color-scheme: dark)').matches
      document.documentElement.classList.toggle('dark', prefersDark)
      setIsDark(prefersDark)
    } else {
      window.localStorage.setItem('sv-theme', next)
      document.documentElement.classList.toggle('dark', next === 'dark')
      setIsDark(next === 'dark')
    }
  }

  async function onClickLogout() {
    await apiLogout()
    SentryClient.setUser(null)
    router.update({
      context: {
        ...router.options.context,
        user: {
          isAuthenticated: false
        }
      }
    })
    await router.navigate({ to: '/login' })
  }

  const handleMenuItemClick = () => {
    if (onMenuItemClick) {
      onMenuItemClick()
    }
  }

  if (!user.isAuthenticated || !user.userOrgProfile) {
    return null
  }

  const initials =
    user.userOrgProfile.first_name && user.userOrgProfile.surname
      ? `${user.userOrgProfile.first_name[0]}${user.userOrgProfile.surname[0]}`
      : 'U'
  const hasScopedOrganisationSettings = isConversationWorkspaceAvailable(user.userOrgProfile.enabled_features)
  const fullName = `${user.userOrgProfile.first_name} ${user.userOrgProfile.surname}`.trim()

  return (
    <SidebarMenu>
      <SidebarMenuItem>
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <SidebarMenuButton
              ref={menuTriggerRef}
              size="lg"
              className="data-[state=open]:bg-sidebar-accent data-[state=open]:text-sidebar-accent-foreground"
            >
              <Avatar className="h-8 w-8 rounded-lg">
                <AvatarImage src={''} alt={user.userOrgProfile.first_name} />
                <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
              </Avatar>
              <span className="min-w-0 flex-1 truncate text-left text-sm font-semibold">{fullName}</span>
              <ChevronsUpDown className="ml-auto size-4" />
            </SidebarMenuButton>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            className="w-(--radix-dropdown-menu-trigger-width) min-w-64 rounded-xl p-1.5"
            side="top"
            align="start"
            sideOffset={8}
          >
            <DropdownMenuItem
              aria-label={`Open profile for ${fullName}`}
              className="gap-3 px-2 py-2.5"
              onSelect={() => {
                menuTriggerRef.current?.focus()
                handleMenuItemClick()
                openSettings({ scope: 'profile', section: 'profile' })
              }}
            >
              <Avatar className="size-9 rounded-lg">
                <AvatarImage src={''} alt="" />
                <AvatarFallback className="rounded-lg">{initials}</AvatarFallback>
              </Avatar>
              <span className="min-w-0 flex-1 truncate font-medium">{fullName}</span>
              <ChevronRight className="size-4 text-muted-foreground" aria-hidden="true" />
            </DropdownMenuItem>
            <DropdownMenuSeparator />
            <DropdownMenuGroup>
              {!hasScopedOrganisationSettings && (
                <DropdownMenuItem asChild onClick={handleMenuItemClick}>
                  <Link
                    to="/$orgSlug/account"
                    params={{ orgSlug: currentOrgSlug }}
                    className={currentPathname.includes('/account') ? 'bg-sidebar-accent/50' : ''}
                  >
                    <BadgeCheck />
                    Account
                  </Link>
                </DropdownMenuItem>
              )}
              {!hasScopedOrganisationSettings && (
                <DropdownMenuItem asChild onClick={handleMenuItemClick}>
                  <Link
                    to="/$orgSlug/users"
                    params={{ orgSlug: currentOrgSlug }}
                    className={currentPathname.includes('/users') ? 'bg-sidebar-accent/50' : ''}
                  >
                    <Users />
                    Users
                  </Link>
                </DropdownMenuItem>
              )}
            </DropdownMenuGroup>
            <DropdownMenuSeparator />
            <DropdownMenuSub>
              <DropdownMenuSubTrigger>
                {isDark ? <Moon /> : <Sun />}
                Theme
              </DropdownMenuSubTrigger>
              <DropdownMenuSubContent>
                <DropdownMenuItem onClick={() => applyTheme('system')}>
                  <Monitor />
                  System
                  {theme === 'system' && <span className="ml-auto text-xs text-muted-foreground">✓</span>}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => applyTheme('light')}>
                  <Sun />
                  Light
                  {theme === 'light' && <span className="ml-auto text-xs text-muted-foreground">✓</span>}
                </DropdownMenuItem>
                <DropdownMenuItem onClick={() => applyTheme('dark')}>
                  <Moon />
                  Dark
                  {theme === 'dark' && <span className="ml-auto text-xs text-muted-foreground">✓</span>}
                </DropdownMenuItem>
              </DropdownMenuSubContent>
            </DropdownMenuSub>
            <DropdownMenuSeparator />
            <DropdownMenuItem onClick={onClickLogout}>
              <LogOut />
              Log out
            </DropdownMenuItem>
          </DropdownMenuContent>
        </DropdownMenu>
      </SidebarMenuItem>
    </SidebarMenu>
  )
}
